import * as React from 'react';
import { createPortal } from 'react-dom';

import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import { TypographyP2Bold } from '../typography';

import {
  fileDropCardLiftClassName,
  fileDropCardMotion,
  fileDropCardSlotClassName,
  fileDropTakeoverVariants,
} from './file-drop-takeover.styles';
import { type FileDropTakeoverProps } from './file-drop-takeover.types';

/**
 * Full-surface file-drop affordance. Presentational only: it never accepts
 * files (`FileDropSurface` owns the drop and the shared validation) and is
 * `pointer-events-none` + `aria-hidden` so it stays out of the accessibility
 * tree while the drop lands on the surface beneath it.
 */
const FileDropTakeover = React.forwardRef<HTMLDivElement, FileDropTakeoverProps>(
  (
    {
      visible,
      scope,
      container,
      inline = false,
      label,
      'data-testid': testId = 'file-drop-takeover',
    },
    ref
  ) => {
    const intl = useDesignSystemIntl();
    const resolvedLabel =
      label ??
      intl.formatMessage({
        defaultMessage: 'Drop files here',
        id: 'AjUPkmI6RU',
        description: 'Default label on the full-surface file-drop overlay',
      });

    if (!visible || typeof document === 'undefined') return null;

    const overlay = (
      <div
        ref={ref}
        aria-hidden
        data-testid={testId}
        className={fileDropTakeoverVariants({ scope })}
      >
        <style>{fileDropCardMotion}</style>
        <div className="flex flex-col items-center gap-4">
          <div className="relative size-20">
            <div className={fileDropCardSlotClassName} />
            <div className={fileDropCardLiftClassName}>
              <svg
                width="46"
                height="14"
                viewBox="0 0 46 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-text-tertiary"
                aria-hidden
              >
                <path d="M1 3c7.09-3.57 7.17 4.13 14.35 0" />
                <path d="M16.5 3c7.09-3.57 7.17 4.13 14.35 0" />
                <path d="M1 11c7.09-3.57 7.17 4.13 14.35 0" />
                <path d="M16.5 11c7.09-3.57 7.17 4.13 14.35 0" />
                <path d="M31 11c7.09-3.57 7.17 4.13 14.35 0" />
              </svg>
              <div className="flex size-6 items-center justify-center self-center rounded-md border border-border-hover text-text-primary">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  aria-hidden
                >
                  <path d="M6 2v8M2 6h8" />
                </svg>
              </div>
            </div>
          </div>
          <TypographyP2Bold className="text-text-primary">
            {resolvedLabel}
          </TypographyP2Bold>
        </div>
      </div>
    );

    if (inline) return overlay;

    const portalTarget = scope === 'window' ? document.body : container;
    if (!portalTarget) return null;

    return createPortal(overlay, portalTarget);
  }
);
FileDropTakeover.displayName = 'FileDropTakeover';

export { FileDropTakeover };
